<template>
  <div>
    <v-card
      flat
      class="mt-2"
    >
      <v-card-text>
        <div v-if="!hasAllocations" class="text-center py-4 grey--text">
          {{ $t('noAllocationsToChart') }}
        </div>
        <div v-else style="max-height: 800px; min-height: 200px; position: relative;">
          <div style="height: 500px; width: 100%;">
            <canvas ref="chartCanvas" style="width: 100%; height: 100%;"/>
          </div>
          <div v-if="lastUpdatedRelative" class="text-right mt-2 grey--text text--caption">
            {{ lastUpdatedRelative }}
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { Chart } from 'chart.js'
import { getChartColors } from '@/components/dashboard/ChartColors'
import { chartTooltipStyle } from '@/utils/ChartHelpers'
import { numberWithCommas } from '@/utils/NumericMutations'
import { NumericConstants } from '@/utils/constants'
import { utcToLocalDate } from '@/utils/DateFormatter'

export default {
  name: 'AllocationChart',

  props: {
    volumeBasedCruiseData: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    chart: null
  }),

  computed: {
    lastUpdatedOn () {
      const dates = this.volumeBasedCruiseData.flatMap(cruise => {
        const cruiseDate = cruise.harvestUpdatedOn || cruise.serverObject?.harvestUpdatedOn
        const allocDates = (cruise.tractContentAllocations || cruise.serverObject?.tractContentAllocations || [])
          .map(alloc => alloc.harvestUpdatedOn)
          .filter(Boolean)
        return [cruiseDate, ...allocDates].filter(Boolean)
      })

      if (!dates.length) return null

      // Return the most recent date in ISO format
      return dates
        .map(d => new Date(d))
        .sort((a, b) => b - a)[0].toISOString()
    },

    lastUpdatedRelative () {
      if (!this.lastUpdatedOn) return null
      const updated = utcToLocalDate(new Date(this.lastUpdatedOn), 'raw')
      const now = new Date()

      const diffMs = now - updated

      const seconds = Math.floor(diffMs / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 0) return `${this.$t('lastUpdatedXago', { x: `${days} ${days === 1 ? 'day' : 'days'}` })}`
      if (hours > 0) return `${this.$t('lastUpdatedXago', { x: `${hours} ${hours === 1 ? 'hour' : 'hours'}` })}`
      if (minutes > 0) return `${this.$t('lastUpdatedXago', { x: `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` })}`
      return this.$t('lastUpdatedX', { x: this.$t('justNow') })
    },

    hasAllocations () {
      return this.volumeBasedCruiseData.some(tc => (tc.tractContentAllocations || []).length > 0)
    },

    chartData () {
      const sortedCruiseData = [...this.volumeBasedCruiseData].sort((a, b) =>
        (a.species || '').localeCompare(b.species || '')
      )

      const destinationSet = new Set()
      sortedCruiseData.forEach(tc => {
        (tc.tractContentAllocations || []).forEach(alloc => {
          if (alloc.destinationAccountName) destinationSet.add(alloc.destinationAccountName)
        })
      })
      const destinations = Array.from(destinationSet)

      const colors = getChartColors(sortedCruiseData.length)

      const datasets = sortedCruiseData.map((tc, index) => {
        const data = destinations.map(dest => {
          const alloc = (tc.tractContentAllocations || []).find(a => a.destinationAccountName === dest)
          if (!alloc) return 0
          return Math.max(alloc.expected - alloc.harvested, 0) / NumericConstants.MbfDivisor
        })
        return {
          label: tc.species,
          data,
          backgroundColor: colors[index],
          borderWidth: 1,
          borderColor: '#FFFFFF'
        }
      }).filter(ds => ds.data.some(val => val !== null && val !== 0))

      return {
        labels: destinations,
        datasets
      }
    }
  },

  watch: {
    volumeBasedCruiseData: {
      handler () {
        this.$nextTick(() => this.renderChart())
      },
      deep: true
    }
  },

  mounted () {
    this.renderChart()
  },

  beforeDestroy () {
    this.destroyChart()
  },

  methods: {
    numberWithCommas,

    destroyChart () {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },

    renderChart () {
      this.destroyChart()

      if (!this.hasAllocations || !this.$refs.chartCanvas) return

      this.chart = new Chart(this.$refs.chartCanvas, {
        type: 'bar',
        data: this.chartData,
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: { family: 'Roboto, sans-serif' },
                usePointStyle: true,
                padding: 16
              }
            },
            tooltip: {
              ...chartTooltipStyle,
              callbacks: {
                label: (context) => {
                  const val = numberWithCommas(context.parsed.x, 3)
                  return ` ${context.dataset.label}: ${val} MBF`
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.$t('remainingMBF'),
                font: { family: 'Roboto, sans-serif', weight: 'bold' }
              },
              ticks: {
                font: { family: 'Roboto, sans-serif' }
              },
              beginAtZero: true
            },
            y: {
              ticks: {
                font: { family: 'Roboto, sans-serif' }
              }
            }
          },
          categoryPercentage: 0.75,
          barPercentage: 0.95,
          maxBarThickness: 40
        }
      })
    },

    refresh () {
      this.$nextTick(() => this.renderChart())
    }
  }
}
</script>
